import { useDraggable, useDroppable } from "@dnd-kit/core";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Clock,
  ListChecks,
  Loader2,
  MoreVertical
} from "lucide-react";
import {
  memo,
  useEffect,
  useRef,
  useState,
  type FC,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode
} from "react";

import { FileTypeIcon } from "@/components/common/FileTypeIcon";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { formatProvenanceDate } from "../../draftVersioning/provenance";
import type { DropPlacement } from "../files/treeRows";
import { iconFileFor, type IconFile } from "./iconFile";
import type { WsNode } from "./types";
import {
  rowTitle,
  type WorkspaceItem,
  type WorkspaceStatus,
  type WsRow
} from "./workspaceModel";

export type { IconFile };
export { iconFileFor };

const INDENT = 14;

export const STATUS_LABEL: Record<WorkspaceStatus, string> = {
  idle: "Not started",
  waiting: "Waiting for analysis",
  running: "Generating",
  ready: "Ready",
  stale: "Needs update",
  failed: "Failed"
};

const RowIcon: FC<{ node: WsNode }> = ({ node }) => {
  const file = iconFileFor(node);
  if (!file) return <ListChecks className="size-3.5 shrink-0 text-fg-3" />;
  return <FileTypeIcon name={file.name} isFolder={file.isFolder} size={14} />;
};

export const StatusDot: FC<{ status: WorkspaceStatus }> = ({ status }) => {
  if (status === "running")
    return (
      <Loader2
        className="size-3 shrink-0 animate-spin text-primary"
        aria-label="Generating"
      />
    );
  if (status === "waiting")
    return (
      <Clock
        className="size-3 shrink-0 text-fg-3"
        aria-label={STATUS_LABEL.waiting}
      />
    );
  if (status === "failed")
    return (
      <AlertTriangle
        className="size-3 shrink-0 text-danger"
        aria-label="Failed"
      />
    );
  return (
    <span
      aria-label={STATUS_LABEL[status]}
      className={cn(
        "size-1.5 shrink-0 rounded-full",
        status === "ready" && "bg-success",
        status === "stale" && "bg-warn",
        status === "idle" && "bg-fg-4"
      )}
    />
  );
};

export type WorkspaceRowProps = {
  row: WsRow;
  /** The item's copy in Case Files ("Add to Case Files"), with its exhibit label. */
  caseFileCopy?: { label: string | null } | null;
  selected: boolean;
  focused: boolean;
  renaming: boolean;
  /** Where a drag currently hovers over this row, if any. */
  dropHint: DropPlacement | null;
  dragging: boolean;
  onToggle: (id: string) => void;
  onClick: (row: WsRow, e: MouseEvent) => void;
  onDoubleClick: (row: WsRow) => void;
  onContextMenu: (row: WsRow, e: MouseEvent) => void;
  /** The three-dot menu is open for this row. */
  actionsOpen?: boolean;
  onActionsOpenChange?: (row: WsRow, open: boolean) => void;
  /** Menu body, mounted while `actionsOpen`. Same items as right-click. */
  actionsMenu?: ReactNode;
  onRenameCommit: (id: string, name: string) => void;
  onRenameCancel: () => void;
  onKeyDown: (row: WsRow, e: KeyboardEvent) => void;
};

const RenameInput: FC<{
  initial: string;
  onCommit: (name: string) => void;
  onCancel: () => void;
}> = ({ initial, onCommit, onCancel }) => {
  const [value, setValue] = useState(initial);
  const ref = useRef<HTMLInputElement>(null);
  // The menu that opened this field moves focus in the same turn. Ignore that
  // blur, then take focus once it has settled.
  const armed = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.select();
    armed.current = true;
  }, [initial]);
  const finish = (commit: boolean) => {
    if (commit && value.trim() && value.trim() !== initial)
      onCommit(value.trim());
    else onCancel();
  };
  return (
    <input
      ref={ref}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onBlur={() => {
        if (!armed.current) return;
        finish(true);
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter") {
          e.preventDefault();
          armed.current = false;
          finish(true);
        } else if (e.key === "Escape") {
          e.preventDefault();
          armed.current = false;
          onCancel();
        }
      }}
      className="h-5 min-w-0 flex-1 rounded-xs border border-primary bg-elevated px-1 text-base outline-none"
      aria-label="Rename"
    />
  );
};

/**
 * When a written draft last changed: "Nov 2" for the row, "Last updated Nov
 * 2, 2026" for its tooltip. The list does not carry who generated a draft;
 * the open draft says so in its title bar.
 */
const draftUpdated = (
  item: WorkspaceItem
): { short: string; sentence: string } | null => {
  if (item.artifact.kind !== "draft") return null;
  if (item.status !== "ready" && item.status !== "stale") return null;
  const full = formatProvenanceDate(item.updatedAt);
  if (!full || !item.updatedAt) return null;
  const date = new Date(item.updatedAt);
  const short =
    date.getFullYear() === new Date().getFullYear()
      ? date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
      : full;
  return { short, sentence: `Last updated ${full}` };
};

/** True when the row's title can be edited in place: folders, drafts and plans. */
export const canRenameRow = (row: WsRow): boolean =>
  row.node.kind === "folder" ||
  row.node.kind === "draft" ||
  row.node.kind === "plan";

export const WorkspaceRow = memo(function WorkspaceRow({
  row,
  caseFileCopy,
  selected,
  focused,
  renaming,
  dropHint,
  dragging,
  onToggle,
  onClick,
  onDoubleClick,
  onContextMenu,
  actionsOpen = false,
  onActionsOpenChange,
  actionsMenu = null,
  onRenameCommit,
  onRenameCancel,
  onKeyDown
}: WorkspaceRowProps) {
  const { node, item, depth, hasChildren, expanded } = row;
  const isFolder = node.kind === "folder";
  const {
    setNodeRef: setDragRef,
    listeners,
    attributes
  } = useDraggable({
    id: `workspace:${node.id}`,
    data: { tree: "workspace", nodeId: node.id, row },
    disabled: renaming
  });
  const { setNodeRef: setDropRef } = useDroppable({
    id: `workspace:${node.id}`,
    data: { tree: "workspace", row }
  });

  const title = rowTitle(row);
  const revealed = selected || focused || actionsOpen;
  const updated = item ? draftUpdated(item) : null;
  const detail = caseFileCopy
    ? caseFileCopy.label
      ? `In Case Files · ${caseFileCopy.label}`
      : "In Case Files"
    : item?.detail || updated?.short;
  // A plan's detail already reads "Building · 1 of 4 done"; the generic
  // "Generating" is for artifacts a run produces.
  const tooltip = item
    ? `${title} · ${
        node.kind === "plan" && item.detail
          ? item.detail
          : STATUS_LABEL[item.status]
      }${updated ? ` · ${updated.sentence}` : ""}`
    : title;

  return (
    <div
      ref={(el) => {
        setDragRef(el);
        setDropRef(el);
      }}
      {...attributes}
      {...listeners}
      role="treeitem"
      aria-label={tooltip}
      aria-level={depth + 1}
      aria-expanded={isFolder ? expanded : undefined}
      aria-selected={selected}
      data-node-id={node.id}
      tabIndex={focused ? 0 : -1}
      onClick={(e) => onClick(row, e)}
      onDoubleClick={() => onDoubleClick(row)}
      onContextMenu={(e) => onContextMenu(row, e)}
      onKeyDown={(e) => onKeyDown(row, e)}
      title={renaming ? undefined : tooltip}
      className={cn(
        "group relative flex h-(--row-h) w-full items-center gap-1.5 pr-2 text-base text-fg-2 outline-none select-none hover:pr-8",
        revealed && "pr-8",
        selected ? "bg-fill-2 text-fg" : "hover:bg-fill-1",
        focused && "ring-1 ring-inset ring-primary/40",
        dragging && "opacity-40",
        dropHint?.kind === "inside" &&
          "bg-accent-soft ring-1 ring-inset ring-primary"
      )}
      style={{ paddingLeft: 6 + depth * INDENT }}
    >
      {dropHint?.kind === "before" ? (
        <div className="pointer-events-none absolute inset-x-1 top-0 h-0.5 bg-primary" />
      ) : null}
      {dropHint?.kind === "after" ? (
        <div className="pointer-events-none absolute inset-x-1 bottom-0 h-0.5 bg-primary" />
      ) : null}

      <button
        type="button"
        tabIndex={-1}
        aria-label={expanded ? "Collapse" : "Expand"}
        onClick={(e) => {
          e.stopPropagation();
          if (isFolder) onToggle(node.id);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        className={cn(
          "-mr-0.5 flex size-4 shrink-0 items-center justify-center rounded-xs text-fg-4 hover:bg-fill-2 hover:text-fg-2",
          (!isFolder || !hasChildren) && "invisible"
        )}
      >
        {expanded ? (
          <ChevronDown className="size-3.5" />
        ) : (
          <ChevronRight className="size-3.5" />
        )}
      </button>

      <RowIcon node={node} />
      {item ? <StatusDot status={item.status} /> : null}

      {renaming ? (
        <RenameInput
          initial={title}
          onCommit={(name) => onRenameCommit(node.id, name)}
          onCancel={onRenameCancel}
        />
      ) : (
        <span className={cn("min-w-0 flex-1 truncate", isFolder && "text-fg")}>
          {title}
        </span>
      )}

      {!renaming && item && detail ? (
        <span
          className="max-w-[45%] shrink-0 truncate text-sm text-fg-4"
          data-testid={caseFileCopy ? "workspace-row-copy" : undefined}
        >
          {detail}
        </span>
      ) : null}

      {!renaming ? (
        <DropdownMenu
          open={actionsOpen}
          onOpenChange={(open) => onActionsOpenChange?.(row, open)}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  tabIndex={-1}
                  aria-label={`Actions for ${title}`}
                  data-testid="workspace-row-actions"
                  data-revealed={revealed ? "true" : "false"}
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  onDoubleClick={(e) => e.stopPropagation()}
                  className={cn(
                    "absolute top-1/2 right-1 z-10 -translate-y-1/2",
                    "pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100",
                    "focus-visible:pointer-events-auto focus-visible:opacity-100",
                    revealed && "pointer-events-auto opacity-100",
                    revealed ? "bg-fill-2" : "group-hover:bg-fill-1"
                  )}
                >
                  <MoreVertical className="size-3.5" />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="top">More Actions</TooltipContent>
          </Tooltip>
          {actionsMenu}
        </DropdownMenu>
      ) : null}
    </div>
  );
});
